/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2024, Zumper
 */

/* istanbul ignore file */

import type { RentReport } from '@zcode/bizos'
import { currencyFormatter } from '@zcode/utils'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

export interface RentReportComponentProps {
  rentReport: RentReport
}

export const RentReportComponent = ({ rentReport }: RentReportComponentProps) => {
  const [supportsScope, setSupportsScope] = useState(
    typeof window === 'undefined' ? true : !!window.CSSScopeRule
  )
  useEffect(() => {
    setSupportsScope(!!window.CSSScopeRule)
  }, [])
  return (
    <div className="w-full">
      <style
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
        dangerouslySetInnerHTML={{
          __html: supportsScope ? `@scope {${rentReport.css}}` : rentReport.css,
        }}
      />
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
      <div dangerouslySetInnerHTML={{ __html: rentReport.html }} />
      {rentReport.fullData && (
        <>
          <h2 className="display32To40 mx-auto max-w-[936px] mt-[60px] mb-5">Full Data</h2>
          <div className="w-full overflow-x-auto max-w-full">
            <table className="m-auto w-full max-w-[936px]">
              <caption className="sr-only">Full Data table</caption>
              <thead className="bg-blue-900 text-gray-0 ">
                <tr className="uppercase">
                  <th className="p-2 py-2.5" colSpan={3} />
                  <th className="p-2 py-2.5" colSpan={3}>
                    1 Bedroom
                  </th>
                  <th className="p-2 py-2.5" colSpan={3}>
                    2 Bedrooms
                  </th>
                </tr>
                <tr>
                  <th className="p-2">Ranking</th>
                  <th className="p-2">Ranking Change</th>
                  <th className="p-2">City</th>
                  <th className="p-2">Price</th>
                  <th className="p-2">M/M%</th>
                  <th className="p-2">Y/Y%</th>
                  <th className="p-2">Price</th>
                  <th className="p-2">M/M%</th>
                  <th className="p-2">Y/Y%</th>
                </tr>
              </thead>
              <tbody>
                {rentReport.fullData.map(
                  ({
                    city,
                    monthChange1,
                    monthChange2,
                    ranking,
                    rankingChange,
                    price1,
                    price2,
                    url,
                    yearChange1,
                    yearChange2,
                  }) => (
                    <tr className="border-gray-150 border-solid border-t odd:bg-gray-0" key={url}>
                      <td className="p-2">{ranking}</td>
                      <td className="p-2">{rankingChange}</td>
                      <td className="p-2">
                        <Link className="underline" to={`/apartments-for-rent/${url}`}>
                          {city}
                        </Link>
                      </td>
                      <td className="p-2">
                        {currencyFormatter(price1, { showCents: 'exceptZero', symbol: '$' })}
                      </td>
                      <td className="p-2">{(monthChange1 * 100).toFixed(2)}%</td>
                      <td className="p-2">{(yearChange1 * 100).toFixed(2)}%</td>
                      <td className="p-2">
                        {currencyFormatter(price2, { showCents: 'exceptZero', symbol: '$' })}
                      </td>
                      <td className="p-2">{(monthChange2 * 100).toFixed(2)}%</td>
                      <td className="p-2">{(yearChange2 * 100).toFixed(2)}%</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
